"use client";

import React from "react";
import { S3Image } from "@/components/common/S3Image";
import Link from "next/link";
import type {
  ContentWithImageSectionData,
  SectionProps,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * epona ContentWithImage — text + image, side by side.
 *
 * Forked from the default renderer for one reason: when the image slot is
 * empty (e.g. a department with no group photo yet), the default collapses
 * to a single column. Here we render a branded Epona placeholder panel
 * (deep-blue gradient + medical cross-hatch + a picture glyph) so the
 * two-column composition stays balanced and reads as "photo coming", not
 * broken. Everything else mirrors the default look.
 */

const CROSS_PATTERN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Cpath d='M22 14v16 M14 22h16' stroke='%23ffffff' stroke-width='1.1' stroke-linecap='round' opacity='0.18'/%3E%3C/svg%3E\")";

function ImagePlaceholder({ label }: { label?: string }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-2xl"
      style={{
        backgroundImage: "var(--gradient-primary)",
        aspectRatio: "3 / 2",
      }}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundImage: CROSS_PATTERN, backgroundSize: "44px 44px" }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 80% at 50% 30%, rgba(255,255,255,0.14), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
        <svg
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.6" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
        {label && (
          <span
            className="text-xs uppercase tracking-widest"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

export function ContentWithImageSection({
  section,
  currentLanguage = "en",
}: SectionProps<ContentWithImageSectionData>) {
  const { content, layout = "imageLeft" } = section;

  // Tolerate nested (wizard/canonical) and flat (CMS editor) shapes.
  const descObj = (content as any)?.description ?? content;
  const imgUrl = (content as any)?.image?.url ?? (section as any).image ?? "";
  const imgAltObj = (content as any)?.image?.alt ?? (section as any).imageAlt;
  const button = (content as any)?.button ?? (section as any).button;
  const description = descObj ? getLocalizedText(descObj, currentLanguage) : "";
  // Heading block: `headline` renders as the gold eyebrow, `content.title` as
  // the section title. Both optional so existing sections look unchanged.
  const headline = (section as { headline?: Parameters<typeof getLocalizedText>[0] }).headline;
  const eyebrow = headline ? getLocalizedText(headline, currentLanguage) : "";
  const title = content?.title ? getLocalizedText(content.title, currentLanguage) : "";
  // Set by applyDeptBindings when a department's history is truncated for the
  // home page — links to the full /dept/<slug>/history page. Only show the link
  // when THIS language's text was actually clipped (ends with the ellipsis the
  // binder appends), so a short translation doesn't get a needless "Read more".
  const readMoreHref = (content as { readMoreHref?: string })?.readMoreHref;
  const showReadMore = !!readMoreHref && description.trimEnd().endsWith("…");
  const readMoreLabel = currentLanguage === "mm" ? "ဆက်ဖတ်ရန်" : "Read more";
  const imageAlt = imgAltObj
    ? getLocalizedText(imgAltObj, currentLanguage)
    : "Image";
  const hasImage = !!imgUrl;
  // Optional video in the media slot (`content.video` or flat `section.video`):
  // plays inline instead of the photo; the photo doubles as the poster.
  const video = (content as any)?.video ?? (section as any).video;
  const videoUrl: string = video?.url || "";
  const videoPoster: string | undefined = video?.poster || imgUrl || undefined;
  const pos =
    section.imagePosition ?? (layout === "imageRight" ? "right" : "left");
  const isStacked = pos === "top" || pos === "bottom";
  const imgAlignClass =
    (section.imageAlign ?? "center") === "left"
      ? "mr-auto"
      : (section.imageAlign ?? "center") === "right"
        ? "ml-auto"
        : "mx-auto";

  return (
    <section
      className="py-16 px-4 bg-gradient-to-br from-background/50 to-background"
      data-section-id={section._id}
      data-section-type={section.type}
    >
      <div className="max-w-6xl mx-auto">
        <div
          className={`grid gap-12 items-center ${
            isStacked ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
          }`}
        >
          {/* Image (or branded placeholder) */}
          <div
            className={
              isStacked
                ? `w-full max-w-3xl ${imgAlignClass} ${
                    pos === "bottom" ? "order-2" : "order-1"
                  }`
                : pos === "right"
                  ? "lg:order-2"
                  : ""
            }
          >
            {videoUrl ? (
              // Data-driven media slot: `content.video.url` (+ optional poster)
              // plays inline in place of the photo.
              <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-background p-2">
                <video
                  src={videoUrl}
                  poster={videoPoster}
                  title={imageAlt}
                  controls
                  playsInline
                  preload="metadata"
                  className="block aspect-video w-full rounded-xl bg-black object-contain"
                />
              </div>
            ) : hasImage ? (
              <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-background p-2 transform hover:scale-105 transition-transform duration-300">
                <S3Image
                  src={imgUrl as string}
                  alt={imageAlt}
                  className="w-full h-auto object-cover rounded-xl"
                  width={600}
                  height={400}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-xl pointer-events-none"></div>
              </div>
            ) : (
              <ImagePlaceholder />
            )}
          </div>

          {/* Content */}
          <div
            className={`space-y-6 ${
              isStacked
                ? pos === "bottom"
                  ? "order-1"
                  : "order-2"
                : pos === "right"
                  ? "lg:order-1"
                  : ""
            }`}
          >
            {(eyebrow || title) && (
              <div className="space-y-3">
                {eyebrow && <span className="ci926-section-eyebrow">{eyebrow}</span>}
                {title && (
                  <h2
                    className="ci926-section-title text-3xl md:text-4xl"
                    style={{ color: "var(--color-primary)" }}
                  >
                    {title}
                  </h2>
                )}
              </div>
            )}
            <div className="space-y-4">
              {description.split("\n").filter((p) => p.trim()).map((paragraph, index) => (
                <p
                  key={index}
                  className="text-lg text-muted-foreground leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}

              {showReadMore && (
                <Link
                  href={readMoreHref!}
                  className="inline-flex items-center gap-1.5 font-semibold text-primary hover:gap-2.5 transition-all"
                  style={{ color: "var(--color-primary)" }}
                >
                  {readMoreLabel}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              )}

              {button && (
                <div className="pt-4">
                  <Link
                    href={button.url}
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-md shadow-lg hover:from-primary/90 hover:to-primary/70 transform hover:scale-105 transition-all duration-200 focus:ring-4 focus:ring-primary/30 focus:outline-none"
                  >
                    {getLocalizedText(button.text, currentLanguage)}
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContentWithImageSection;
